import CompanyMap, { ICompanyMapHandles } from '@components/views/mapbox/CompanyMap';
import { Box, IconButton, Toolbar } from '@mui/material';
import { IStoreState } from '@store/index';
import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import SitesMapRightPanel from './SitesMapRightPanel';
import StreamMarker from '@classes/MapBox/StreamMarker';
import SaveBar from '@components/common/SaveBar';
import { orderBy } from 'lodash';
import mapboxgl from 'mapbox-gl';
import API from '@API/index';
import { useDispatch } from 'react-redux';
import { setStreamList } from '@store/streams/actions';
import { useSnackbar } from 'notistack';
import Utils from '@utils/index';
import { Edit } from '@mui/icons-material';

const Container = styled(Box) <{ sidebar: boolean }>`
  display: grid;
  grid-template-columns: ${props => props.sidebar ? 'auto min-content 25vw' : 'auto min-content 0px'};
  transition: 300ms;
`

const SitesMap: React.FC<{}> = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const selectedCompany = useSelector((store: IStoreState) => store.selectedCompanies.selectedCompany);
  const sites = useSelector((store: IStoreState) => store.sites.sites);
  const selectedSite = useSelector((store: IStoreState) => store.selectedSites.selectedSite);
  const streams = useSelector((store: IStoreState) => store.streams.streams);

  const [isEditing, setEditing] = React.useState<boolean>(false);
  const [markers, setMarkers] = React.useState<StreamMarker[]>([]);
  const [focusedStream, setFocusedStream] = React.useState<string | undefined>(undefined);

  const companyMapRef = React.useRef<ICompanyMapHandles | null>(null);

  const handleStreamClicked = React.useCallback((stream: VideoStream) => {
    if (companyMapRef.current) {
      companyMapRef.current.addStream(stream);
    }
  }, [])

  const handleDiscardChanges = React.useCallback(() => {
    if (selectedSite) {
      setFocusedStream(undefined);
      const siteStreams = streams.filter(el => el.siteId === selectedSite.siteId);
      const removed: string[] = [];
      siteStreams.forEach((stream) => {
        const target = markers.find(el => el.getKvsName() === stream.kvsName);
        if (target) {
          if (!stream.latitude || !stream.longitude) {
            target.cleanUp();
            target.remove();
            removed.push(target.getKvsName())
            if (companyMapRef.current) {
              companyMapRef.current.removeMarker(stream);
            }
          } else {
            target.cleanUp();
            target.setLngLat(new mapboxgl.LngLat(stream.longitude, stream.latitude));
            target.repaintLabel();
            target.paintFov(stream.range || 100, stream.angleEastOfNorth || 0, stream.fovWidth || 70)
          }
        }
      })
      setMarkers(markers.filter(el => !removed.includes(el.getKvsName())))

    }
  }, [markers, selectedSite, streams])

  const handleSaveChanges = React.useCallback((callback?: () => void) => {
    if (selectedSite) {
      const promises: Promise<any>[] = [];
      const changedStreams: VideoStream[] = [];

      const siteStreams = streams.filter(el => el.siteId === selectedSite.siteId);
      siteStreams.forEach((stream) => {
        const target = markers.find(el => el.getKvsName() === stream.kvsName);
        if (target &&
          (target.getLngLat().lat !== stream.latitude ||
            target.getLngLat().lng !== stream.longitude ||
            target.getAngleEastOfNorth() !== stream.angleEastOfNorth ||
            target.getRange() !== stream.range ||
            target.getFovWidth() !== stream.fovWidth)) {
          changedStreams.push({
            ...stream,
            latitude: target?.getLngLat().lat || 0,
            longitude: target?.getLngLat().lng || 0,
            angleEastOfNorth: target.getAngleEastOfNorth(),
            fovWidth: target.getFovWidth(),
            range: target.getRange()
          });
          promises.push(API.Streams.updateStream(
            {
              kvsName: stream.kvsName,
              siteId: stream.siteId,
              companyId: stream.companyId,
              latitude: target?.getLngLat().lat || 0,
              longitude: target?.getLngLat().lng || 0,
              angleEastOfNorth: target.getAngleEastOfNorth(),
              fovWidth: target.getFovWidth(),
              range: target.getRange()
            } as any
          ))
        }
      })

      Promise.all(promises)
        .then(() => {
          dispatch(setStreamList(streams.map((stream) => {
            const target = changedStreams.find(el => stream.kvsName === el.kvsName);
            if (target) {
              return { ...stream, latitude: target.latitude, longitude: target.longitude, angleEastOfNorth: target.angleEastOfNorth, fovWidth: target.fovWidth, range: target.range }
            }
            return stream;
          })))
          enqueueSnackbar("Positions saved!", { variant: 'success' })
        })
        .catch((error) => {
          console.log(error);
          enqueueSnackbar(Utils.Error.getErrorMessage(error), { variant: 'error' })
        }).finally(() => {
          if (callback) callback()
        })
    }
  }, [dispatch, enqueueSnackbar, markers, selectedSite, streams])

  const currentCameraPositions = React.useMemo(() => {
    const currentPositions: { kvsName: string, latitude?: number, longitude?: number, angleEastOfNorth?: number, fovWidth?: number, range?: number }[] = [];

    streams.filter(el => el.siteId === selectedSite?.siteId && el.latitude && el.longitude).forEach((stream) => {
      currentPositions.push({
        kvsName: stream.kvsName,
        latitude: stream.latitude,
        longitude: stream.longitude,
        angleEastOfNorth: stream.angleEastOfNorth,
        fovWidth: stream.fovWidth,
        range: stream.range
      })
    })

    return orderBy(currentPositions, 'kvsName');
  }, [selectedSite?.siteId, streams])

  const newPositions = React.useMemo(() => {
    const newPositions: { kvsName: string, latitude?: number, longitude?: number, angleEastOfNorth?: number, fovWidth?: number, range?: number }[] = [];

    markers.forEach((marker) => {
      newPositions.push({
        kvsName: marker.getKvsName(),
        latitude: marker.getLngLat().lat,
        longitude: marker.getLngLat().lng,
        angleEastOfNorth: marker.getAngleEastOfNorth(),
        fovWidth: marker.getFovWidth(),
        range: marker.getRange()
      })
    })

    return orderBy(newPositions, 'kvsName');
  }, [markers])

  const changed = React.useMemo(() => {
    if (newPositions.length === 0) return false;

    if (JSON.stringify(currentCameraPositions) !== JSON.stringify(newPositions)) {
      return true;
    }

    return false;
  }, [currentCameraPositions, newPositions])

  const multipleSites = React.useMemo(() => {
    const companySites: Site[] = sites.get(selectedCompany?.companyId || "") || [];

    return { sites: companySites };
  }, [selectedCompany?.companyId, sites])

  const mapHeight = React.useMemo(() => {
    const element = document.getElementById("active-nav-bar");
    if (element) {
      return window.innerHeight - element.scrollHeight;
    }

    return "94.5vh"
  }, [])

  React.useEffect(() => {
    if (selectedSite === undefined) setEditing(false);
  }, [selectedSite])

  React.useEffect(() => {
    if (selectedSite === undefined || isEditing === false) setFocusedStream(undefined)
  }, [isEditing, selectedSite])

  return (
    <Box width="100%" height="100%">
      <Container sidebar={Boolean(selectedSite && isEditing)} >
        <CompanyMap
          ref={companyMapRef}
          height={mapHeight}
          multiplesites={multipleSites}
          onMarkersUpdated={(markers) => { setMarkers(markers) }}
          enableEdit={isEditing}
          focusedStream={focusedStream}
          onMarkerFocus={(marker: StreamMarker) => {
            console.log(marker.getKvsName())
            setFocusedStream(marker.getKvsName())
          }}
        />
        <Box
          height="94.5vh"
          borderLeft="1px solid rgba(0, 0, 0, 0.12)"
          borderRight="1px solid rgba(0, 0, 0, 0.12)"
          padding="0.2rem"
          display="flex"
          flexDirection="column"
          alignItems="center"
          width="3rem"
        >
          <Toolbar title="Edit positions">
            <span>
              <IconButton
                onClick={() => setEditing(!isEditing)}
                disabled={!Boolean(selectedSite)}
              >
                <Edit />
              </IconButton>
            </span>
          </Toolbar>
        </Box>
        <SitesMapRightPanel
          onStreamClicked={handleStreamClicked}
          streamMarkers={markers}
          focusedStream={focusedStream}
        />
      </Container>
      <SaveBar
        open={changed && selectedSite !== undefined && isEditing === true}
        onDiscardChanges={handleDiscardChanges}
        onSaveChanges={handleSaveChanges}
      />
    </Box>
  )
}

export default SitesMap;
